import gql from "graphql-tag";
import {useCallback} from "react";
import type {Editor} from "slate";

import {useInterval} from "@khanacademy/wonder-blocks-timing";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {useMutation} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {getCoeditorVariables} from "@khan/guide-coeditor-core";
import {useAIGuideToolPersona} from "@khan/guide-core-helpers";
import {useGuideState} from "@khan/guide-state";
import {Errors, KAError} from "@khan/logging";
import {useLocation} from "@khan/urls";

import shouldAutosaveCoeditor from "../internal/should-autosave-coeditor.ts";
import {clearUnsavedChanges} from "../slate/helpers/insertion-helpers.ts";
import type {AutosavingStatus} from "../types.ts";

import type {coeditingAutosaveType} from "./__graphql-types__/index.ts";

export const COEDITING_AUTOSAVE_MUTATION: GqlOperation<
    coeditingAutosaveType["response"],
    coeditingAutosaveType["variables"]
> = gqlOp(gql`
    mutation coeditingAutosave(
        $configName: String!
        $threadId: String!
        $title: String!
        $prefix: String!
        $selection: String!
        $suffix: String!
        $persona: AIGuidePersona!
    ) {
        saveCoeditingSnapshot(
            configName: $configName
            threadId: $threadId
            title: $title
            prefix: $prefix
            selection: $selection
            suffix: $suffix
            persona: $persona
        ) {
            error {
                code
            }
        }
    }
`);

export default function useCoeditorAutosave(
    editor: Editor | null,
    documentTitle: string,
    setSavingStatus?: (savingStatus: AutosavingStatus) => void,
) {
    const threadId = useGuideState("threadId");
    const navigation = useGuideState("navigation");
    const configName = useGuideState("selectedBotName");
    const {pathname} = useLocation();
    const persona = useAIGuideToolPersona();
    const [mutate] = useMutation(COEDITING_AUTOSAVE_MUTATION);

    const doAutosaveMutation = useCallback(async () => {
        if (
            !shouldAutosaveCoeditor(threadId, pathname, editor) ||
            !setSavingStatus
        ) {
            return;
        }

        setSavingStatus("loading");

        const {prefix, selection, suffix} = getCoeditorVariables();

        const result = await mutate({
            configName: configName ?? "",
            threadId: threadId ?? "",
            title: documentTitle ?? "",
            prefix: prefix ?? "",
            selection: selection ?? "",
            suffix: suffix ?? "",
            persona: persona,
        });

        // If we have an error status or we got a successful response but
        // it has an error in it, then we throw an error.
        // We should never see the loading status here since this waits
        // for the mutation to be complete, and we the aborted status is
        // something we can ignore for this case.
        const error =
            result.status === "error"
                ? result.error
                : result.status === "success" &&
                    result.data.saveCoeditingSnapshot.error
                  ? result.data.saveCoeditingSnapshot.error.code
                  : null;

        publishAnalyticsEventForMinorConversion({
            eventType: "AiGuideCoeditorDocumentSaveRequested",
            eventSchemaVersion: 1,
            aiGuideThreadId: threadId ?? "",
            aiGuideNavigationId: navigation.id,
            saveSuccessful: !error,
        });

        if (error) {
            setSavingStatus("error");

            throw new KAError("Failed to autosave coediting", Errors.Internal, {
                cause: error,
            });
        }

        setSavingStatus("saved");

        clearUnsavedChanges(editor!);
    }, [
        threadId,
        pathname,
        editor,
        setSavingStatus,
        mutate,
        configName,
        documentTitle,
        persona,
        navigation.id,
    ]);

    useInterval(doAutosaveMutation, 5000);
}
